import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isStaffRole } from '../lib/roles';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const user = await exchangeSession(sessionId);
          
          // Check if profile needs completion (for new Google users)
          if (user.profile_incomplete && user.role === 'customer') {
            toast.info('Please complete your profile to continue', {
              description: 'We need a few more details to set up your account.'
            });
            navigate('/complete-profile', { replace: true, state: { user, isNewGoogleUser: user.is_new_user } });
            return;
          }
          
          // Redirect based on role
          if (user.role === 'investor') {
            navigate('/investor/dashboard', { replace: true, state: { user } });
          } else if (isStaffRole(user.role)) {
            navigate('/staff/dashboard', { replace: true, state: { user } });
          } else {
            navigate('/customer/dashboard', { replace: true, state: { user } });
          }
        } catch (error) {
          console.error('Session exchange failed:', error);
          toast.error('Sign in failed. Please try again.');
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [location, exchangeSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-900 mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
